"use client"
import DirectImage from "@/components/ui/direct-image"
import { useCart } from "@/lib/context/cart-context"
import { useSpecials } from "@/lib/context/specials-context"
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Minus, Plus, X, ShoppingBag, Store, Truck } from "lucide-react"
import Image from "next/image"
import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { CheckoutModal } from "@/components/checkout/checkout-modal"
import CartUpsell from "./cart-upsell"
import { createDutchieCheckoutUrl } from "@/lib/api/dutchie"
import Link from "next/link"
import { getPricePreTax } from "@/lib/format-text"

export default function CartDrawer() {
  const router = useRouter()
  const { cart, isOpen, closeCart, removeFromCart, updateQuantity, fulfillmentType, setFulfillmentType, checkoutUrl, isGeneratingUrl, checkoutPricing } = useCart()
  const { bundleProducts, saleProducts } = useSpecials()
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  // We used to detect mobile vs desktop for different checkout experiences
  // Now we use the same universal checkout for all devices
  useEffect(() => {
    // For compatibility with other parts of the app that might need the mobile detection
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase()
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent)
      const isSmallScreen = window.innerWidth <= 768
      setIsMobile(isMobileDevice || isSmallScreen)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Simple universal checkout redirect - opens Dutchie in new tab for ALL devices
  const handleCheckout = async () => {
    // Validate cart items before checkout
    const hasInvalidItems = cart.items.some(item => !item.variantId);
    
    if (hasInvalidItems) {
      alert("Some items in your cart are missing required information. Please try removing and adding them again.");
      return;
    }
    
    if (cart.items.length === 0) {
      alert("Your cart is empty. Please add items before checkout.");
      return;
    }
    
    // Check if we have a pre-generated URL ready
    if (!checkoutUrl) {
      if (isGeneratingUrl) {
        alert("Preparing your checkout, please wait a moment...");
        return;
      } else {
        alert("Unable to prepare checkout. Please try again.");
        return;
      }
    }
    
    // Universal redirect for ALL devices - simple and reliable!
    console.log(`🚀 Opening Dutchie checkout in new tab - ${fulfillmentType} with ${cart.items.length} items`);
    console.log(`🔗 Checkout URL: ${checkoutUrl}`);
    
    closeCart();
    
    // Direct redirect to Dutchie in new window/tab
    window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
  }

  // Calculate pre-tax subtotal
  const preTaxSubtotal = cart.items.reduce((sum, item) => sum + getPricePreTax(item.price) * item.quantity, 0);
  const hasSaleItems = cart.items.some((item) => saleProducts.get(item.productId));
  const hasBundleItems = cart.items.some((item) => bundleProducts.get(item.productId));
  const savingsLabel = hasSaleItems && !hasBundleItems
    ? 'Sale Savings'
    : hasBundleItems && !hasSaleItems
      ? 'Bundle Savings'
      : 'Savings Applied';

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-3 border-b pb-3">
          <SheetTitle className="flex justify-between items-center">
            <span className="text-xl font-bold">Your Cart</span>
            <span className="text-sm text-gray-500">
              {cart.items.length} {cart.items.length === 1 ? 'item' : 'items'}
            </span>
          </SheetTitle>
        </SheetHeader>

        {cart.items.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-600">Your cart is empty</p>
            <Button onClick={closeCart} className="mt-4 bg-primary_green hover:bg-primary_green/90">
              Continue Shopping
            </Button>
          </div>
        ) : (
          <>
            {/* Shopping info section */}
            <div className="bg-gray-50 p-3 rounded-md mb-4">
              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-500">Shopping at</p>
                    <p className="font-medium">716 Cannabis</p>
                  </div>
                  <div className="text-xs text-gray-400">
                    Subtotal updates automatically
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      if (fulfillmentType !== 'pickup') {
                        console.log(`🔄 Switching fulfillment type from ${fulfillmentType} to pickup`)
                        setFulfillmentType('pickup')
                      }
                    }}
                    disabled={isGeneratingUrl}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${
                      fulfillmentType === 'pickup'
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Store className="h-4 w-4" />
                    Pickup
                  </button>
                  <button
                    onClick={() => {
                      if (fulfillmentType !== 'delivery') {
                        console.log(`🔄 Switching fulfillment type from ${fulfillmentType} to delivery`)
                        setFulfillmentType('delivery')
                      }
                    }}
                    disabled={isGeneratingUrl}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${
                      fulfillmentType === 'delivery'
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Truck className="h-4 w-4" />
                    Delivery
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-5 my-4">
              {cart.items.map((item) => {
                // Check if this item has a bundle deal or sale
                const itemBundleDeal = bundleProducts.get(item.productId);
                const itemSaleDeal = saleProducts.get(item.productId);
                const hasDeal = itemBundleDeal || itemSaleDeal;
                
                return (
                <div key={item.variantId} className="border-b pb-4">
                  <div className="flex flex-col space-y-2">
                    <div className="flex justify-between">
                      <div className="flex">
                        {item.image && (
                          <div className="mr-3 flex-shrink-0 relative">
                            <DirectImage 
                              src={item.image} 
                              alt={item.name} 
                              width={48} 
                              height={48} 
                              className="rounded-lg object-cover border border-gray-100 shadow-sm bg-white"
                            />
                            {/* Deal badge on image */}
                            {itemSaleDeal && (
                              <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-full leading-none">
                                {itemSaleDeal.dealFormat || "Sale"}
                              </div>
                            )}
                            {itemBundleDeal && !itemSaleDeal && (
                              <div className="absolute -top-1 -right-1 bg-purple-600 text-white text-[8px] font-bold px-1 py-0.5 rounded-full leading-none">
                                {itemBundleDeal.dealFormat || "Deal"}
                              </div>
                            )}
                          </div>
                        )}
                        <div>
                          <h3 className="font-medium text-sm">{item.name}</h3>
                          {item.option && (
                            <span className="text-gray-600 text-xs">{item.option}</span>
                          )}
                          {/* Deal info under product name */}
                          {itemSaleDeal && (
                            <div className="text-[10px] text-red-600 font-medium mt-0.5">
                              {itemSaleDeal.dealFormat} applied at checkout
                            </div>
                          )}
                          {itemBundleDeal && !itemSaleDeal && (
                            <div className="text-[10px] text-purple-600 font-medium mt-0.5">
                              {itemBundleDeal.dealFormat} eligible
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.variantId)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="flex border rounded-md">
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <div className="px-4 py-1 border-l border-r flex items-center justify-center">
                            {item.quantity}
                          </div>
                          <button
                            onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                            className="px-2 py-1 text-gray-600 hover:bg-gray-100"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">${(getPricePreTax(item.price) * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Upsell: "You may also like" tuned to what's in the cart */}
            <CartUpsell />

            {/* Order Summary */}
            <div className="mt-6 pt-4 border-t bg-gray-50 -mx-6 px-6 pb-6">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
              
              {/* Pricing breakdown */}
              {checkoutPricing ? (
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal ({cart.items.length} items)</span>
                    <span className="text-gray-900">${checkoutPricing.subtotal.toFixed(2)}</span>
                  </div>
                  
                  {checkoutPricing.discounts > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 font-medium">{savingsLabel}</span>
                      <span className="text-green-600 font-medium">-${checkoutPricing.discounts.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Estimated Tax</span>
                    <span className="text-gray-900">${checkoutPricing.taxes.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="font-bold text-xl text-gray-900">${checkoutPricing.total.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal ({cart.items.length} items)</span>
                    <span className="text-gray-900">${preTaxSubtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="text-gray-500 italic">Calculated at checkout</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <span className="font-bold text-gray-900">Estimated Total</span>
                    <span className="font-bold text-xl text-gray-900">${preTaxSubtotal.toFixed(2)}+</span>
                  </div>
                </div>
              )}
              
              {/* Action buttons */}
              <div className="space-y-2">
                <Button 
                  onClick={handleCheckout} 
                  disabled={isGeneratingUrl || !checkoutUrl}
                  className="w-full bg-primary_green hover:bg-primary_green/90 disabled:opacity-50 disabled:cursor-not-allowed h-12 text-base font-semibold"
                >
                  {isGeneratingUrl ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Preparing...
                    </>
                  ) : (
                    'Checkout'
                  )}
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={closeCart} 
                  className="w-full text-gray-600 hover:bg-gray-100 border-gray-300"
                >
                  Continue Shopping
                </Button>
              </div>
              
              {/* Security badge */}
              <div className="flex items-center justify-center mt-4 pt-4 border-t border-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                  <path d="M9 12l2 2 4-4"></path>
                </svg>
                <span className="text-xs text-gray-500">Secure checkout powered by Dutchie</span>
              </div>
            </div>
          </>
        )}
        {/* Universal checkout experience for all devices - mobile modal removed */}
      </SheetContent>
    </Sheet>
  )
}
